/**
 * API service for stock screener data
 */

import axios from 'axios'
import { API_BASE_URL } from '../config'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout for bulk data
})

// Add request interceptor to include JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

/**
 * Fetch all stock screener data for TanStack Table
 * @returns {Promise<Object>} Response with stock data array
 */
export const fetchAllScreenerData = async () => {
  try {
    const response = await api.get('/api/stocks/screener-data/')
    return response.data
  } catch (error) {
    console.error('Error fetching screener data:', error)
    throw error
  }
}

/**
 * Refresh the screener cache (admin only)
 * @returns {Promise<Object>} Response with cache status
 */
export const refreshScreenerCache = async () => {
  try {
    const response = await api.post('/api/admin/refresh-screener-cache/')
    return response.data
  } catch (error) {
    // Surface HTTP status code to caller for graceful fallback
    error.status = error?.response?.status
    throw error
  }
}

/**
 * Process natural language chat query for screening
 * @param {string} query - Natural language query
 * @returns {Promise<Object>} Response with filtered results
 */
export const processChatQuery = async (query) => {
  try {
    const response = await api.post('/api/stocks/chat-query/', {
      query: query
    })
    return response.data
  } catch (error) {
    console.error('Error processing chat query:', error)
    throw error
  }
}

/**
 * Fetch symbol groups (watchlists) from the API
 * @param {boolean} [dynamic] - If true, only fetch dynamic watchlists (adds ?dynamic=true query parameter)
 * @returns {Promise<Array>} Array of symbol group objects
 */
export const fetchSymbolGroups = async (dynamic = false) => {
  try {
    const url = dynamic ? '/api/symbol-groups/?dynamic=true' : '/api/symbol-groups/'
    const response = await api.get(url)
    return response.data
  } catch (error) {
    console.error('Error fetching symbol groups:', error)
    throw error
  }
}

/**
 * Fetch stocks from the API based on search query
 * @param {string} searchQuery - Search query for stock symbols
 * @returns {Promise<Array>} Array of stock objects
 */
export const fetchStocks = async (searchQuery) => {
  if (!searchQuery || searchQuery.trim() === '') {
    return []
  }
  try {
    // Use the DRF API endpoint for symbols with search parameter
    const response = await api.get(`/api/symbols/?symbol=${encodeURIComponent(searchQuery)}`)
    // DRF ViewSet returns results in response.data.results for paginated responses
    // or response.data directly for non-paginated
    const data = response.data
    return Array.isArray(data) ? data : (data?.results || [])
  } catch (error) {
    console.error('Error fetching stocks:', error)
    throw error
  }
}

/**
 * Fetch event types from the API
 * @returns {Promise<Array>} Array of event type objects with value and label
 */
export const fetchEventTypes = async () => {
  try {
    const response = await api.get('/api/screener/event-types/')
    return response.data
  } catch (error) {
    console.error('Error fetching event types:', error)
    throw error
  }
}

/**
 * Fetch quantitative alert choices from the API
 * @returns {Promise<Object>} Object with alert type configurations
 */
export const fetchQuantitativeAlertChoices = async () => {
  try {
    const response = await api.get('/api/alerts/quantitative-alert-choices/')
    return response.data
  } catch (error) {
    console.error('Error fetching quantitative alert choices:', error)
    throw error
  }
}

/**
 * Fetch technical analysis alert choices from the API
 * @returns {Promise<Object>} Object with patterns, chart_time_horizons, and running_time_sections
 */
export const fetchTechnicalAnalysisAlertChoices = async () => {
  try {
    const response = await api.get('/api/alerts/technical-analysis-alert-choices/')
    return response.data
  } catch (error) {
    console.error('Error fetching technical analysis alert choices:', error)
    throw error
  }
}

/**
 * Fetch alert category configuration from the API
 * @param {string} category - Alert category type (quantitative, event, technical_analysis, content, screener)
 * @returns {Promise<Object>} Object with input_details and additional_info (for quantitative)
 */
export const fetchAlertCategory = async (category) => {
  try {
    const response = await api.get(`/api/alerts/alert-category/?type=${category}`)
    // API returns paginated response with results array
    if (response.data.results && response.data.results.length > 0) {
      return response.data.results[0]
    }
    throw new Error(`No configuration found for category: ${category}`)
  } catch (error) {
    console.error(`Error fetching alert category ${category}:`, error)
    throw error
  }
}

/**
 * Fetch all alerts for the authenticated user
 * @returns {Promise<Array>} Array of alert objects
 */
export const fetchAlerts = async () => {
  try {
    const response = await api.get('/api/alerts/')
    return response.data.data || []
  } catch (error) {
    console.error('Error fetching alerts:', error)
    throw error
  }
}

/**
 * Create a new alert
 * @param {Object} alertData - Alert data to create
 * @returns {Promise<Object>} Response with data and message
 */
export const createAlert = async (alertData) => {
  try {
    const response = await api.post('/api/alerts/', alertData)
    return {
      data: response.data.data,
      message: response.data.message,
      status: response.status
    }
  } catch (error) {
    console.error('Error creating alert:', error)
    // Extract error message and status from response
    const errorMessage = error.response?.data?.message || error.message || 'Failed to create alert'
    const status = error.response?.status || 500
    throw {
      message: errorMessage,
      status: status,
      originalError: error
    }
  }
}

export default api
